import {
  Dialog,
  Icon,
  Icons,
  IconSize,
  Placement,
  Popover,
  Result
} from '@yy/tofu-ui-react';
import axios from 'axios';
import classNames from 'classnames';
import React from 'react';
import { UserRoleContext } from '../App';
import { GuildZhuYeItem } from '../models/GuildZhuYeResult';
import { GuildInfo } from '../models/QueryGuildInfoResult';
import {
  convertHttps,
  dynamicImportImages,
  isGuild,
  shortZhNum,
  tsSep,
  hiddoClick
} from '../util';
import MoreInfoContent from './MoreInfoContent';
import RankBadge from './RankBadge';

const queryGuildInfoURL = '/zone/guildanchor/guildZhuYe.json';

async function fetchGuildInfo(id: number): Promise<GuildInfo[]> {
  const { data } = await axios.get(queryGuildInfoURL, {
    params: { pageIndex: 1, pageSize: 10, locType: 4, id }
  });

  const list = data?.list;
  if (list) {
    return list.map((x: GuildInfo) => ({
      ...x,
      yynum: x.linkUrl,
      avatar: convertHttps(x.picUrl),
      link: `//www.yy.com/gu/${x.linkUrl}`
    }));
  } else {
    throw new Error(
      `fetchGuildInfo can't fetch list, returns: ${fetchGuildInfo}`
    );
  }
}

const GuildInfoItem: React.FC<{
  item: GuildInfo;
  idx: number;
  showRank: boolean;
}> = ({ item, idx, showRank }) => {
  const order = idx + 1;
  const rankNumImg = dynamicImportImages(`./no.${order}@2x.png`);
  return (
    <li
      className={classNames(
        'mien-item-other__item',
        order <= 3 ? `mien-item-other__item-${order}` : ''
      )}
      style={{
        marginBottom: idx <= 1 ? 10 : 0
      }}
      key={item.yynum}
    >
      <span
        className="rank-num"
        style={{
          backgroundImage: `url(${rankNumImg})`
        }}
      />
      <div
        className="mien-item-other__item__avatar"
        style={{ backgroundImage: `url(${item.avatar})` }}
        onClick={() => {
          hiddoClick('11');
          window.open(item.link, '_blank');
        }}
      />
      <div className="mien-item-other__item__info">
        <div className="mien-item-other__item__name">
          <span style={{ marginRight: 10 }}>{item.name}</span>
          {!!(showRank && item.rank > 0) && (
            <RankBadge rank={item.rank} id={`rank-badge-${item.id}`} />
          )}
        </div>

        <div className="mien-item-other__item-extra">
          <span>YY号: {item.yynum}</span>
          <span>旗下主播: {tsSep(shortZhNum(`${item.anchorCount}`))}</span>
        </div>
      </div>
    </li>
  );
};

interface GuildMienState {
  showRankMap: Map<string, string>;
  /** 详情链接 */
  guildInfoLinksMap: Map<string, string>;
  guildInfosMap: { string?: number | GuildInfo[] };
  dialogVisible: boolean;
  errorDialog: { show: boolean; msg: string };
  chatDialog: { show: boolean };
}

export default class GuildMien extends React.Component<{}, GuildMienState> {
  state = {
    showRankMap: new Map(),
    guildInfoLinksMap: new Map(),
    guildInfosMap: {},
    dialogVisible: false,
    errorDialog: { show: false, msg: '' },
    chatDialog: { show: false }
  };
  fetchAllIDs = async () => {
    // 拿到所有类型的id先
    const { data } = await axios.get(queryGuildInfoURL, {
      params: { pageIndex: 1, pageSize: 4, locType: 4, id: 0 }
    });

    let list = data?.list;
    if (list && list.length) {
      // 只取3个排行榜
      list = list.slice(0, 3);
      const initMap = list.reduce(
        (acc: object, { name, id }: GuildZhuYeItem) => {
          if (name === '优质公会') return acc; // 排除无用数据
          return { ...acc, [name]: id };
        },
        {}
      );
      const linksMap = list.reduce(
        (acc: object, { name, linkUrl }: GuildZhuYeItem) => {
          if (name === '优质公会') return acc; // 排除无用数据
          return { ...acc, [name]: linkUrl };
        },
        {}
      );
      const rankMap = list.reduce(
        (acc: object, { name, showRank }: GuildZhuYeItem) => {
          if (name === '优质公会') return acc; // 排除无用数据
          return { ...acc, [name]: showRank };
        },
        {}
      );
      this.setState({
        guildInfosMap: initMap,
        guildInfoLinksMap: new Map(Object.entries(linksMap)),
        showRankMap: new Map(Object.entries(rankMap))
      });
      return Promise.resolve();
    }
    return Promise.reject('fetchAllIDs list empty');
  };

  fetchGuildInfos = async () => {
    const { guildInfosMap } = this.state;

    for (const [name, id] of Object.entries(guildInfosMap)) {
      let guildInfos = await fetchGuildInfo(id as number);
      if (guildInfos && guildInfos.length) {
        guildInfos = guildInfos.slice(0, 10);
        this.setState((state) => ({
          guildInfosMap: {
            ...state.guildInfosMap,
            [name]: guildInfos
          }
        }));
      }
    }
  };

  showErrorDialog = (msg: string) => {
    this.setState({ errorDialog: { show: true, msg } });
  };

  showChatWarnDialog = () => {
    this.setState({ chatDialog: { show: true } });
  };

  hideErrorDialog = () => {
    this.setState({ errorDialog: { show: false, msg: '' } });
  };

  hideChatDialog = () => {
    this.setState({ chatDialog: { show: false } });
  };

  componentDidMount() {
    this.fetchAllIDs().then(() => {
      this.fetchGuildInfos();
    });
  }

  showDialog = () => {
    this.setState({ dialogVisible: true });
  };

  hideDialog = () => {
    this.setState({ dialogVisible: false });
  };

  onBeAnchorClick = () => {
    this.hideDialog();
    window.open('//www.yy.com/i/index/anchor?beAnchor=1', '_blank');
  };

  render() {
    const {
      guildInfosMap,
      dialogVisible,
      guildInfoLinksMap,
      showRankMap,
      errorDialog,
      chatDialog
    } = this.state;

    return (
      <UserRoleContext.Consumer>
        {(userRole) => (
          <div className="mien-contain">
            <Dialog
              active={dialogVisible}
              onCancel={this.hideDialog}
              onOk={this.onBeAnchorClick}
              okTxt="成为主播"
            >
              <Result
                className="dialogResult"
                status="fail"
                title="您不是主播，不能签约！"
                subTitle="请先成为主播，再申请签约。"
              />
            </Dialog>

            <Dialog
              active={errorDialog.show}
              onCancel={this.hideErrorDialog}
              onOk={this.hideErrorDialog}
              okTxt="确定"
            >
              <Result
                className="dialogResult"
                status="warning"
                title="无法调起应聘咨询"
                subTitle={errorDialog.msg}
              />
            </Dialog>

            <Dialog
              active={chatDialog.show}
              onCancel={this.hideChatDialog}
              onOk={this.hideChatDialog}
              showConfirmBtn={false}
              cancelTxt="知道了"
            >
              <Result
                className="dialogResult"
                status="warning"
                title=""
                subTitle="该公会为普通公会，不支持应聘咨询会话"
              />
            </Dialog>

            <p className="mien-top-title">公会风采</p>

            <ul className="mien-list">
              {Object.entries(guildInfosMap).map(([name, data]) => {
                const showRank = showRankMap.get(name);
                return (
                  <li className="mien-item" key={name}>
                    <p className="mien-item__title">
                      <span className="mien-item__title__label" />
                      <span className="mien-item__title__txt">{name}</span>
                      {guildInfoLinksMap.get(name) && (
                        <a
                          href={guildInfoLinksMap.get(name)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon type={Icons.Question} size={IconSize.Small} />
                        </a>
                      )}
                    </p>
                    {Array.isArray(data) && (
                      <div className="mien-item-body">
                        <ul className="mien-item-other__list">
                          {data.map((item: GuildInfo, idx: number) =>
                            isGuild(userRole) ? (
                              <GuildInfoItem
                                item={item}
                                idx={idx}
                                key={idx}
                                showRank={showRank}
                              />
                            ) : (
                              <Popover
                                key={idx}
                                placement={Placement.Right}
                                showArrow={false}
                                reference={
                                  <GuildInfoItem
                                    item={item}
                                    idx={idx}
                                    key={idx}
                                    showRank={showRank}
                                  />
                                }
                              >
                                <MoreInfoContent
                                  showDialog={this.showDialog}
                                  guildYY={item.yynum}
                                  guildUid={item.description}
                                  showErrorDialog={this.showErrorDialog}
                                  showChatWarnDialog={this.showChatWarnDialog}
                                />
                              </Popover>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </UserRoleContext.Consumer>
    );
  }
}
